import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Paper, Step,
  StepLabel, Stepper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, MenuItem, Link,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { blAPI, marcheAPI } from '../services/api';
import { apiError, dateFr, dateHeureFr, heure, parsePoleNumbers, BL_STATUS } from '../utils/format';

const LEVELS = ['1 · Stock', '2 · Logistique', '3 · Contrôle indépendant'];

const Info = ({ label, value }) => (
  <Box sx={{ display: 'flex', mb: 1 }}>
    <Typography sx={{ width: 170, color: 'text.secondary' }}>{label}</Typography>
    <Typography sx={{ fontWeight: 500 }}>{value || '—'}</Typography>
  </Box>
);

const BonLivraison = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, canViewDashboard, canCompta: canInvoice, isComptable, canMarches } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [lien, setLien] = useState(null); // rattachement BC / ADL : { marche_id, adl_id, choix }

  const load = useCallback(() => {
    blAPI.get(id).then((r) => setData(r.data)).catch((err) => setError(apiError(err)));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <Layout title="Bon de livraison">{error ? <Alert severity="error">{error}</Alert> : 'Chargement…'}</Layout>;

  const { delivery: d, lines, validations, invoice, next_level: nextLevel } = data;
  const alreadyValidated = validations.some((v) => v.user_id === user?.id);
  const canValidate = !isComptable && nextLevel && !alreadyValidated && (nextLevel < 3 || canViewDashboard)
    && !(nextLevel === 3 && d.created_by === user?.id);
  const ecart = lines.some((l) => l.ecart !== 0);
  const editable = !isComptable && d.status === 'EN_COURS' && validations.length === 0;

  const run = async (fn, okMsg) => {
    setBusy(true); setError(''); setMessage('');
    try {
      const r = await fn();
      setMessage(r?.data?.message || okMsg);
      load();
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const openEdit = () => {
    setForm({
      destination: d.destination || '', commande_ref: d.commande_ref || '',
      transporteur: d.transporteur || '', chauffeur: d.chauffeur || '', permis_conduire: d.permis_conduire || '',
      immatriculation: d.immatriculation || '', telephone_chauffeur: d.telephone_chauffeur || '',
      heure_entree: heure(d.heure_entree), heure_sortie: heure(d.heure_sortie),
      numbers: Object.fromEntries(lines.map((l) => [l.pba_type, l.numeros.join('-')])),
    });
    setEditOpen(true);
  };

  const saveEdit = () => run(async () => {
    const { numbers, ...rest } = form;
    const r = await blAPI.update(id, {
      ...rest, pole_numbers: Object.fromEntries(Object.entries(numbers).map(([t, v]) => [t, parsePoleNumbers(v)])),
    });
    setEditOpen(false);
    return r;
  });

  const f = (name, label, props = {}) => (
    <TextField fullWidth size="small" label={label} value={form[name] || ''} sx={{ mb: 2 }}
      onChange={(e) => setForm((p) => ({ ...p, [name]: e.target.value }))} {...props} />
  );

  return (
    <Layout title={`Bon de livraison ${d.bl_numero || ''}`}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip label={BL_STATUS[d.status]?.label} color={BL_STATUS[d.status]?.color} />
        <Typography color="text.secondary" sx={{ mr: 'auto' }}>Émis le {dateHeureFr(d.emis_le)}</Typography>
        <Button variant="outlined" onClick={() => window.open(`/bons-livraison/${id}/imprimer`, '_blank')}>Imprimer (3 exemplaires)</Button>
        {editable && <Button variant="outlined" onClick={openEdit}>Compléter / corriger</Button>}
        {canViewDashboard && d.status === 'EN_COURS' && (
          <Button color="error" variant="outlined" disabled={busy}
            onClick={() => window.confirm(`Annuler ${d.bl_numero} ? Les poteaux reviendront en stock.`) && run(() => blAPI.cancel(id))}>
            Annuler le BL
          </Button>
        )}
        {canInvoice && d.status === 'VALIDEE' && !invoice && (
          <Button variant="contained" color="success" onClick={() => navigate(`/factures/nouvelle?bl=${id}`)}>Facturer</Button>
        )}
        {invoice && canInvoice && (
          <Button onClick={() => navigate(`/factures/${invoice.id}`)}>Facture {invoice.numero || '(brouillon)'}</Button>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline">Livraison</Typography>
            <Info label="Client" value={d.client_name} />
            <Info label="Date de livraison" value={dateFr(d.delivery_date)} />
            <Info label="Destination" value={d.destination} />
            <Info label="Commande / réf." value={d.commande_ref} />
            <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
              <Typography sx={{ width: 170, color: 'text.secondary' }}>BC client · ADL</Typography>
              <Typography sx={{ fontWeight: 500, flex: 1 }}>
                {d.marche_numero ? (canMarches
                  ? <Link component="button" onClick={() => navigate(`/marches/${d.marche_id}`)}>BC{d.marche_rang ? ` n°${d.marche_rang}` : ''} {d.marche_numero}</Link>
                  : `BC ${d.marche_numero}`) : '—'}
                {' · '}{d.adl_numero ? `ADL ${String(d.adl_numero).padStart(3, '0')}-${d.adl_annee}` : 'sans ADL'}
              </Typography>
              {canMarches && d.status !== 'ANNULEE' && (
                <Button size="small" onClick={async () => {
                  const r = await marcheAPI.choix(d.client_id).catch(() => ({ data: { marches: [], adls: [] } }));
                  setLien({ marche_id: d.marche_id || '', adl_id: d.adl_id || '', choix: r.data });
                }}>Modifier</Button>
              )}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline">Transport / chauffeur</Typography>
            <Info label="Transporteur" value={d.transporteur} />
            <Info label="Chauffeur" value={d.chauffeur} />
            <Info label="Permis / Immatric." value={[d.permis_conduire, d.immatriculation].filter(Boolean).join(' · ')} />
            <Info label="Téléphone" value={d.telephone_chauffeur} />
            <Info label="Entrée / sortie usine" value={[heure(d.heure_entree), heure(d.heure_sortie)].filter(Boolean).join(' → ')} />
            {(data.garde || []).map((g, i) => (
              <Info key={i} label={i ? 'Poste de garde (autre)' : 'Poste de garde'}
                value={`${g.entree ? `entrée ${g.entree.slice(8, 10)}/${g.entree.slice(5, 7)} ${g.entree.slice(11, 16)}` : 'entrée non notée'} → ${g.sortie ? `sortie ${g.sortie.slice(8, 10)}/${g.sortie.slice(5, 7)} ${g.sortie.slice(11, 16)}` : 'pas encore sorti'}${g.resultat ? ` · ${{ AUTORISEE: 'sortie autorisée', ECART: 'sortie avec écart', REFUSEE: 'sortie refusée', NON_CONTROLE: 'sortie non contrôlée' }[g.resultat]}` : ''}${g.immatriculation ? ` · ${g.immatriculation}` : ''}`} />
            ))}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline">Désignations des poteaux livrés</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Désignation (type homologué)</TableCell>
                  <TableCell align="right">Qté</TableCell>
                  <TableCell>Numéros des poteaux livrés</TableCell>
                  <TableCell>Contrôle</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((l) => (
                  <TableRow key={l.pba_type}>
                    <TableCell>{l.designation}</TableCell>
                    <TableCell align="right"><strong>{l.quantite}</strong></TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 13, maxWidth: 520, wordBreak: 'break-word' }}>
                      {l.numeros.join('-') || <em>aucun numéro saisi</em>}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={l.ecart === 0 ? 'success' : 'warning'}
                        label={l.ecart === 0 ? `${l.numeros.length} listés = déclarés` : `${l.numeros.length} listés ≠ ${l.quantite} déclarés`} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {ecart && d.status === 'EN_COURS' && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Le nombre de numéros ne correspond pas à la quantité : corrigez-le (« Compléter / corriger ») avant la validation.
              </Alert>
            )}
          </Paper>
        </Grid>

        {d.status !== 'ANNULEE' && (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="overline">Validation à 3 niveaux — fonctions séparées</Typography>
              <Stepper activeStep={validations.length} alternativeLabel sx={{ my: 2 }}>
                {LEVELS.map((label, i) => {
                  const v = validations.find((x) => x.niveau === i + 1);
                  return (
                    <Step key={label} completed={!!v}>
                      <StepLabel optional={v ? <Typography variant="caption">{v.nom} — {dateHeureFr(v.valide_le)}</Typography> : null}>
                        {label}
                      </StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
              {nextLevel && (
                <Box sx={{ textAlign: 'center' }}>
                  {canValidate ? (
                    <Button variant="contained" disabled={busy || ecart}
                      onClick={() => window.confirm(`Valider le niveau ${LEVELS[nextLevel - 1]} pour ${d.bl_numero} ?${nextLevel === 3 ? '\nCette validation SCELLE le BL : il ne pourra plus être modifié.' : ''}`)
                        && run(() => blAPI.validate(id, nextLevel))}>
                      Valider : {LEVELS[nextLevel - 1]}{nextLevel === 3 ? ' et sceller' : ''}
                    </Button>
                  ) : (
                    <Typography color="text.secondary">
                      {alreadyValidated
                        ? 'Vous avez déjà validé un niveau : une autre personne doit valider le suivant.'
                        : nextLevel === 3 ? 'Le contrôle indépendant doit être fait par un responsable autre que l\'auteur du BL.'
                          : `En attente : ${LEVELS[nextLevel - 1]}`}
                    </Typography>
                  )}
                </Box>
              )}
              {d.status === 'VALIDEE' && (
                <Alert severity="success" sx={{ mt: 1 }}>
                  Scellé le {dateHeureFr(d.scelle_le)} — code de vérification <strong>{d.code_verification}</strong>
                  <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    Empreinte SHA-256 : {d.empreinte}
                  </Typography>
                </Alert>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>

      <Dialog open={!!lien} onClose={() => setLien(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Bon de commande client et ADL</DialogTitle>
        {lien && (
          <DialogContent sx={{ display: 'grid', gap: 2, pt: '12px !important' }}>
            <TextField select label="Bon de commande client (BC)" value={lien.marche_id} onChange={(e) => setLien({ ...lien, marche_id: e.target.value })}
              helperText={invoice ? 'BL déjà facturé : le BC ne peut plus changer' : 'Seuls les BC ouverts de ce client sont proposés'} disabled={!!invoice}>
              <MenuItem value="">— Aucun —</MenuItem>
              {d.marche_id && !lien.choix.marches.some((m) => m.id === d.marche_id) && <MenuItem value={d.marche_id}>BC {d.marche_numero}</MenuItem>}
              {lien.choix.marches.map((m) => <MenuItem key={m.id} value={m.id}>{m.libelle}</MenuItem>)}
            </TextField>
            <TextField select label="Programme de livraison (ADL)" value={lien.adl_id} onChange={(e) => setLien({ ...lien, adl_id: e.target.value })}>
              <MenuItem value="">— Sans ADL —</MenuItem>
              {d.adl_id && !lien.choix.adls.some((a) => a.id === d.adl_id) && <MenuItem value={d.adl_id}>ADL {String(d.adl_numero).padStart(3, '0')}-{d.adl_annee}</MenuItem>}
              {lien.choix.adls.map((a) => <MenuItem key={a.id} value={a.id}>{a.libelle}</MenuItem>)}
            </TextField>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setLien(null)}>Annuler</Button>
          <Button variant="contained" disabled={busy} onClick={async () => {
            const ok = await run(() => marcheAPI.rattacherBl(id, { marche_id: lien.marche_id || null, adl_id: lien.adl_id || null }));
            if (ok !== false) setLien(null);
          }}>Enregistrer</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Compléter / corriger {d.bl_numero}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {f('destination', 'Destination')}
              {f('commande_ref', 'Commande / réf.')}
              {f('transporteur', 'Transporteur')}
              {f('chauffeur', 'Chauffeur')}
            </Grid>
            <Grid item xs={12} md={6}>
              {f('permis_conduire', 'N° permis de conduire')}
              {f('immatriculation', 'Immatriculation')}
              {f('telephone_chauffeur', 'Téléphone')}
              <Box sx={{ display: 'flex', gap: 2 }}>
                {f('heure_entree', 'Entrée usine', { type: 'time', InputLabelProps: { shrink: true } })}
                {f('heure_sortie', 'Sortie usine', { type: 'time', InputLabelProps: { shrink: true } })}
              </Box>
            </Grid>
          </Grid>
          {lines.map((l) => {
            const count = parsePoleNumbers(form.numbers?.[l.pba_type]).length;
            return (
              <TextField key={l.pba_type} fullWidth multiline minRows={2} size="small" sx={{ mb: 2 }}
                label={`Numéros ${l.pba_type} — ${count} listé(s) / ${l.quantite} déclaré(s)`}
                error={count !== l.quantite}
                value={form.numbers?.[l.pba_type] || ''}
                onChange={(e) => setForm((p) => ({ ...p, numbers: { ...p.numbers, [l.pba_type]: e.target.value } }))} />
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Fermer</Button>
          <Button variant="contained" onClick={saveEdit} disabled={busy}>Enregistrer</Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default BonLivraison;
